import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCaisses, useComptesBancaires } from '@/hooks/queries/useBanques';
import {
  useMarquerPayeFormateur,
  type DossierPaiementMode,
  type PaiementFormateurRow,
} from '@/hooks/queries/usePaiementsFormateurs';
import { MoneyAmount } from '@/components/design-system';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  paiement: PaiementFormateurRow | null;
}

const MODES: { code: DossierPaiementMode; label: string }[] = [
  { code: 'virement', label: 'Virement' },
  { code: 'cheque', label: 'Chèque' },
  { code: 'especes', label: 'Espèces' },
  { code: 'ccp', label: 'CCP' },
  { code: 'carte', label: 'Carte' },
];

export function MarquerPayeFormateurDialog({ open, onOpenChange, paiement }: Props): JSX.Element {
  const marquerPaye = useMarquerPayeFormateur();
  const entrepriseId = paiement?.entreprise_id ?? null;
  const { data: caisses = [] } = useCaisses(entrepriseId);
  const { data: comptes = [] } = useComptesBancaires(entrepriseId);

  const [dateReglement, setDateReglement] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState<DossierPaiementMode>('virement');
  const [reference, setReference] = useState('');
  const [caisseId, setCaisseId] = useState('');
  const [compteId, setCompteId] = useState('');

  useEffect(() => {
    if (!open) return;
    setDateReglement(new Date().toISOString().slice(0, 10));
    setMode('virement');
    setReference('');
    setCaisseId('');
    setCompteId('');
  }, [open]);

  // Auto-sélection du 1er compte/caisse dispo
  useEffect(() => {
    if (mode === 'especes' && caisses.length > 0 && !caisseId) setCaisseId(caisses[0]?.id ?? '');
    if (mode !== 'especes' && comptes.length > 0 && !compteId) setCompteId(comptes[0]?.id ?? '');
  }, [mode, caisses, comptes, caisseId, compteId]);

  if (!paiement)
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );

  const isEspeces = mode === 'especes';
  const targetMissing = isEspeces ? !caisseId : !compteId;

  const handleConfirm = async (): Promise<void> => {
    if (targetMissing) {
      toast.error(isEspeces ? 'Sélectionnez la caisse' : 'Sélectionnez le compte bancaire');
      return;
    }
    try {
      await marquerPaye.mutateAsync({
        paiement,
        dateReglement,
        modePaiement: mode,
        reference: reference.trim() || null,
        caisseId: isEspeces ? caisseId : null,
        compteBancaireId: isEspeces ? null : compteId,
      });
      toast.success(`Paiement marqué payé · opération SORTIE Caisse créée`);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Marquer payé · {paiement.formateur_nom}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-emerald-700">À payer</span>
              <MoneyAmount amount={paiement.montant} variant="large" />
            </div>
            {paiement.formation_titre && (
              <div className="mt-1 text-xs text-slate-600">{paiement.formation_titre}</div>
            )}
          </div>

          <Field label="Date de règlement">
            <input
              type="date"
              value={dateReglement}
              onChange={(e) => setDateReglement(e.target.value)}
              className="input"
              required
            />
          </Field>

          <Field label="Mode de paiement">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {MODES.map((m) => (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => setMode(m.code)}
                  className={cn(
                    'rounded-lg border-2 px-2 py-2 text-xs font-semibold transition-all',
                    mode === m.code
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </Field>

          {isEspeces ? (
            <Field
              label="Caisse débitée"
              hint={
                caisses.length === 0 ? '⚠ Aucune caisse — créez-en une dans Paramètres' : undefined
              }
              warn={caisses.length === 0}
            >
              <select
                value={caisseId}
                onChange={(e) => setCaisseId(e.target.value)}
                className="input"
                disabled={caisses.length === 0}
                required
              >
                <option value="">— Sélectionner —</option>
                {caisses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                    {c.site ? ` · ${c.site}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field
              label="Compte bancaire débité"
              hint={
                comptes.length === 0 ? '⚠ Aucun compte — créez-en un dans Paramètres' : undefined
              }
              warn={comptes.length === 0}
            >
              <select
                value={compteId}
                onChange={(e) => setCompteId(e.target.value)}
                className="input"
                disabled={comptes.length === 0}
                required
              >
                <option value="">— Sélectionner —</option>
                {comptes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.intitule} {c.numero_compte ? `· ${c.numero_compte}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field
            label="Référence opération (optionnelle)"
            hint="Ex. VIR-2026-0042 — auto-généré si vide"
          >
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="input font-mono"
              placeholder="VIR-2026-…"
            />
          </Field>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            Au confirme : 1) opération <strong>SORTIE Caisse</strong> créée (statut : validée), 2)
            paiement passé en statut <strong>payé</strong>, 3) lien croisé entre les deux. Action
            réversible : suppression manuelle de l&apos;opération + reset du statut.
          </div>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={marquerPaye.isPending || targetMissing}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {marquerPaye.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmer le paiement
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">{label}</span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
